import React from 'react';
import { UserPreferences } from '../services/api';

// Top cuisines from the DB tag list (most common first)
export const PREF_CUISINE_OPTIONS = [
  'Европейская кухня', 'Итальянская кухня', 'Японская кухня', 'Русская кухня',
  'Грузинская кухня', 'Азиатская кухня', 'Американская кухня', 'Кавказская кухня',
  'Паназиатская кухня', 'Китайская кухня', 'Авторская кухня', 'Средиземноморская кухня',
  'Корейская кухня', 'Мексиканская кухня', 'Турецкая кухня', 'Узбекская кухня',
];

export const PREF_MENU_OPTIONS = [
  'Пицца', 'Суши', 'Роллы', 'Бургеры', 'Шашлык', 'Паста',
  'Стейки', 'Шаурма', 'Хачапури', 'Рамен', 'Хинкали', 'Плов',
  'На гриле', 'Боулы', 'Салаты', 'Лагман', 'Манты', 'Том-Ям',
];

type TagState = 'neutral' | 'liked' | 'disliked';

interface PreferencesEditorProps {
  value: Omit<UserPreferences, 'isConfigured'>;
  onChange: (value: Omit<UserPreferences, 'isConfigured'>) => void;
}

const HeartIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function getTagState(tag: string, liked: string[], disliked: string[]): TagState {
  if (liked.includes(tag)) return 'liked';
  if (disliked.includes(tag)) return 'disliked';
  return 'neutral';
}

function cycleTag(
  tag: string,
  state: TagState,
  liked: string[],
  disliked: string[],
): { liked: string[]; disliked: string[] } {
  if (state === 'neutral') {
    return { liked: [...liked, tag], disliked };
  } else if (state === 'liked') {
    return { liked: liked.filter((t) => t !== tag), disliked: [...disliked, tag] };
  } else {
    return { liked, disliked: disliked.filter((t) => t !== tag) };
  }
}

const TagChip: React.FC<{
  label: string;
  state: TagState;
  onToggle: () => void;
}> = ({ label, state, onToggle }) => {
  const base = 'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 select-none cursor-pointer active:scale-95';

  const styles: Record<TagState, string> = {
    neutral: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
    liked:   'bg-orange-500 text-white shadow-sm',
    disliked:'bg-gray-100 text-gray-400 line-through',
  };

  return (
    <button type="button" onClick={onToggle} className={`${base} ${styles[state]}`}>
      {state === 'liked' && <HeartIcon className="w-3 h-3 flex-shrink-0" />}
      {state === 'disliked' && <XIcon className="w-3 h-3 flex-shrink-0" />}
      {label}
    </button>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const PreferencesEditor: React.FC<PreferencesEditorProps> = ({ value, onChange }) => {
  const { likedCuisines, dislikedCuisines, likedMenuTags, dislikedMenuTags } = value;

  const toggleCuisine = (tag: string) => {
    const state = getTagState(tag, likedCuisines, dislikedCuisines);
    const next = cycleTag(tag, state, likedCuisines, dislikedCuisines);
    onChange({ ...value, likedCuisines: next.liked, dislikedCuisines: next.disliked });
  };

  const toggleMenu = (tag: string) => {
    const state = getTagState(tag, likedMenuTags, dislikedMenuTags);
    const next = cycleTag(tag, state, likedMenuTags, dislikedMenuTags);
    onChange({ ...value, likedMenuTags: next.liked, dislikedMenuTags: next.disliked });
  };

  return (
    <div className="space-y-6">
      <Section title="Кухни">
        {PREF_CUISINE_OPTIONS.map((tag) => (
          <TagChip
            key={tag}
            label={tag}
            state={getTagState(tag, likedCuisines, dislikedCuisines)}
            onToggle={() => toggleCuisine(tag)}
          />
        ))}
      </Section>

      <Section title="Блюда">
        {PREF_MENU_OPTIONS.map((tag) => (
          <TagChip
            key={tag}
            label={tag}
            state={getTagState(tag, likedMenuTags, dislikedMenuTags)}
            onToggle={() => toggleMenu(tag)}
          />
        ))}
      </Section>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-400 pt-1">
        <span className="flex items-center gap-1">
          <HeartIcon className="w-3 h-3 text-orange-500" />
          нравится
        </span>
        <span className="flex items-center gap-1">
          <XIcon className="w-3 h-3 text-gray-400" />
          не ем
        </span>
        <span className="text-gray-300">· ещё раз — сброс</span>
      </div>
    </div>
  );
};

export default PreferencesEditor;

/** Calculate how well a restaurant matches the user's preferences.
 *  +2 per liked tag match, -1 per disliked tag match. */
export function calcPreferenceScore(
  restaurant: { cuisineTags?: string | null; menuTags?: string | null },
  prefs: Omit<UserPreferences, 'isConfigured'> | null,
): number {
  if (!prefs) return 0;
  const text = `${restaurant.cuisineTags ?? ''} ${restaurant.menuTags ?? ''}`.toLowerCase();
  let score = 0;
  for (const t of prefs.likedCuisines) if (text.includes(t.toLowerCase())) score += 2;
  for (const t of prefs.likedMenuTags) if (text.includes(t.toLowerCase())) score += 2;
  for (const t of prefs.dislikedCuisines) if (text.includes(t.toLowerCase())) score -= 1;
  for (const t of prefs.dislikedMenuTags) if (text.includes(t.toLowerCase())) score -= 1;
  return score;
}
